import {errorMsg, isNotEmpty, isPositiveNumber, sanitizeString} from "../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {ApprovalMaterialRequest} from "../../../../reduxToolkit/MaterialRequest/MaterialRequestApi";
import {toast} from "react-toastify";
import {getToastMessageByStatus} from "../../ApprovalTracker/Model/Model";
import i18next from "i18next";
import {
    getPhotoPicker, normalizePhotoPickerFile
} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import {MIME_TYPE} from "../../../../utils/Base64ToBlob";

/**
 * Material request item model.
 *
 * Represents an individual material item requested by the user.
 * Includes validation logic for required item fields.
 */
export const MATERIAL_REQUEST_ITEM_MODEL = {
    id: null,
    name: '',
    quantity: '',
    measurementUnitId: null,

    isValid() {
        return (
            isNotEmpty(this.name) &&
            isPositiveNumber(this.quantity) &&
            this.measurementUnitId !== null
        );
    }
};

/**
 * Material request model.
 *
 * Represents the main material request entity,
 * including items, evidence files, and request metadata.
 */
export const MATERIAL_REQUEST_MODEL = {
    id: null,
    title: '',
    code: '',
    requestedById: null,
    vehicleInformation: '',
    items: [MATERIAL_REQUEST_ITEM_MODEL],
    evidence: [],
    isValid({ photoPicker, selectedEmployees }) {
        return (
            isNotEmpty(this.title) &&
            isNotEmpty(this.code) &&
            selectedEmployees.length > 0 &&
            photoPicker?.listFiles?.length > 0
        );
    },
    isValidItems() {
        return this.items.filter(e => !e.isValid()).length === 0;
    },
    toRegister({ selectedEmployees, files }) {
        const formData = new FormData();
        formData.append('title', sanitizeString(this.title));
        formData.append('code', sanitizeString(this.code));
        formData.append('requestedById', selectedEmployees[0]?.userId);
        const vehicleInformation = sanitizeString(this.vehicleInformation);
        if(vehicleInformation)
            formData.append('vehicleInformation', vehicleInformation);

        if (Array.isArray(files)) {
            files.forEach((e, index) => {
                formData.append(`evidence[${index}].fileUploadDto.files`, e.fileSelected);
                formData.append(`evidence[${index}].fileUploadDto.userFileNames`, e.fileSelected.name);
            });
        } else if (files) {
            formData.append('evidence[0].fileUploadDto.files', files.fileSelected);
            formData.append('evidence[0].fileUploadDto.userFileNames', files.fileSelected.name);
        }

        this.items.forEach((e, index) => {
            formData.append(`items[${index}].name`, e.name);
            formData.append(`items[${index}].quantity`, e.quantity);
            formData.append(`items[${index}].measurementUnitId`, e.measurementUnitId);
        });
        return formData;
    },
};

/**
 * Material request approval model.
 *
 * Represents approval information for
 * material request approval workflows.
 */
export const MATERIAL_REQUEST_APPROVAL_MODEL = {
    id: null,
    approvalStatusId:null,
    approvalReason: null,
    toRegister: ({ id, approvalStatusId, approvalReason}) => {
        return {
            id: id,
            approvalStatusId: approvalStatusId,
            approvalReason: approvalReason,
        }
    }
}

/**
 * Material request dispatch model.
 *
 * Represents dispatch or discrepancy report
 * information for material requests.
 */
export const MATERIAL_REQUEST_DISPATCH_MODEL = {
    id: null,
    dispatchStatusId:null,
    dispatchReason: null,
    toRegister: ({ id, dispatchStatusId, dispatchReason}) => {
        return {
            id: id,
            dispatchStatusId: dispatchStatusId,
            dispatchReason: dispatchReason,
        }
    }
}

/**
 * Handles material request approval actions.
 *
 * Sends approval information to backend service
 * and displays success or error notifications.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} navigate - Navigation handler.
 * @param {number|string} id - Material request identifier.
 * @param {string} comment - Approval comment.
 * @param {number} status - Approval status identifier.
 * @param {Function} setModified - State updater for refresh control.
 *
 * @returns {void}
 */
export const handleOnClick = (dispatch, navigate, id, comment, status, setModified) => {
    const body = {
        id,
        approvalStatusId: status,
        reason: comment
    };

    dispatch(ApprovalMaterialRequest(body)).then(({ payload: { data: { success, code } } }) => {
        if (success) {
            setModified(true);
            toast.success(i18next.t(getToastMessageByStatus(status)));
        } else {
            errorMsg(code);
        }
    });
}

/**
 * Model that maps submission request statuses to UI configuration.
 *
 * Includes:
 * - Labels
 * - Toast messages
 * - Styles (colors, classes)
 * - Translation keys
 */
export const MATERIAL_REQUEST_CARD_MODEL = {
    APPROVED: {
        id: STATUS_MODEL.APPROVED,
        title : 'approved',
        toast: "approved_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "approved_by"
    },
    DENIED: {
        id: STATUS_MODEL.DENIED,
        title : 'denied',
        toast: "denied_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
        byTrans: "denied_by"
    },
    CHANGES_REQUESTED: {
        id: STATUS_MODEL.CHANGES_REQUESTED,
        title : 'changes_requested',
        toast: "changes_requested_by",
        classCard: "card-warning-150-50",
        fontColor: "font-color-warning-700",
        infoColor: "icon-color-warning-700",
        byTrans: "changes_requested_by"
    },
    DISPATCHED: {
        id: STATUS_MODEL.DISPATCHED,
        title : 'dispatched',
        toast: "dispatched_by",
        classCard: "card-success-150-50",
        fontColor: "font-color-success-700",
        infoColor: "icon-color-success-700",
        byTrans: "dispatched_by"
    },
    DISCREPANCY: {
        id: STATUS_MODEL.DISCREPANCY,
        title : 'discrepancy',
        toast: "discrepancy_by",
        classCard: "card-error-code-150-50",
        fontColor: "font-color-error-600",
        infoColor: "icon-color-error-code-700",
        byTrans: "discrepancy_by"
    },
    /**
     * Retrieves configuration by status ID.
     *
     * @param {number|string} statusId - Status identifier
     * @returns {Object|null} Matching configuration or null if not found
     */
    getById: function (statusId) {
        const row = Object.entries(MATERIAL_REQUEST_CARD_MODEL).find(([ key, data]) => data?.id === parseInt(statusId));
        return row?.[1] || null;
    }
}

export const RECORD_DELIVERY_MODEL ={
    id: null,
    note: null,
    materialRequestItemId: null,
    fileUploadDto: null,
    set(data) {
        return {
            ...this,
            id: data?.itemEvidence?.id || null,
            note: data?.itemEvidence?.note || null,
            materialRequestItemId: data?.id || null,
            fileUploadDto: null,
        }
    },
    isValid({ photoPicker }) {
        return (
            isNotEmpty(this.note) &&
            (this.id || getPhotoPicker(photoPicker) !== null)
        );
    },
    toRegister({ file }) {
        let aux = null;

        const formData = new FormData();
        if(this.id) formData.append('id', this.id );
        formData.append('note', sanitizeString(this.note));
        formData.append('materialRequestItemId', this.materialRequestItemId);

        if(!file.id){
            aux =  normalizePhotoPickerFile(file, MIME_TYPE.PNG);
            formData.append('fileUploadDto.files', aux);
            formData.append('fileUploadDto.userFileNames', aux?.name);
        }else{
            formData.append('fileUploadDto.id', file.id);
        }

        return formData;
    },
    clear() {
        return {
            ...this,
            id: null,
            note: null,
            fileUploadDto: null,
        }
    },
    clearAll() {
        return {
            ...this,
            id: null,
            note: null,
            materialRequestItemId: null,
            fileUploadDto: null,
        }
    },
}

/**
 * Determines whether dispatch badge should be displayed.
 *
 * Badge is hidden for:
 * - Pending approval
 * - Pending dispatch
 *
 * @param {number} dispatchStatusId - Dispatch status identifier.
 *
 * @returns {boolean} Returns true if badge should be visible.
 */
export const showDispatchBadge = (dispatchStatusId) => (
    dispatchStatusId &&
    ![
        STATUS_MODEL.PENDING_TO_APPROVE,
        STATUS_MODEL.PENDING_TO_DISPATCH
    ].includes(dispatchStatusId)
);

/**
 * Retrieves the status identifier displayed
 * for a material request.
 *
 * Rules:
 * - Returns approval status if request is denied.
 * - Otherwise returns dispatch status.
 *
 * @param {Object} item - Material request information.
 *
 * @returns {number|null} Status identifier to display.
 */
export const getMaterialRequestStatusId = (item) => (
    item?.approvalStatusId === STATUS_MODEL.DENIED
        ? item?.approvalStatusId
        : item?.dispatchStatusId
);

/**
 * Determines the dispatch status identifier
 * for pending dispatch validation.
 *
 * Rules:
 * - Returns null when request is already:
 *   - Dispatched
 *   - Reported as discrepancy
 * - Otherwise returns pending status.
 *
 * @param {number} dispatchStatusId - Current dispatch status identifier.
 *
 * @returns {number|null} Dispatch validation status.
 */
export const getMaterialRequestDispatchStatusId = (dispatchStatusId) => (
    [STATUS_MODEL.DISPATCHED, STATUS_MODEL.DISCREPANCY].includes(dispatchStatusId)
        ? null
        : STATUS_MODEL.PENDING
);
